import { useState } from "react";
import { Avatar } from "@/components/shared/Avatar";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { SrIcon } from "@/components/ui/SrIcon";
import { useEmployees } from "@/hooks/use-employees";
import {
  useAddOfferEditor,
  useOfferEditors,
  useRemoveOfferEditor,
} from "@/hooks/use-offer-editors";

interface Props {
  offerId: string;
  ownerId: string;
  /** Kun eier/admin kan legge til og fjerne. */
  canManage: boolean;
}

/**
 * VIB-2099: avatarer for eier + medredigerere. Eier/admin legger til via søk.
 */
export function OfferEditorsPicker({ offerId, ownerId, canManage }: Props) {
  const { data: editors = [] } = useOfferEditors(offerId);
  const { data: employees = [] } = useEmployees();
  const addEditor = useAddOfferEditor(offerId);
  const removeEditor = useRemoveOfferEditor(offerId);
  const [open, setOpen] = useState(false);

  const owner = employees.find((e) => e.id === ownerId);
  const editorUsers = editors
    .map((row) => employees.find((e) => e.id === row.user_id))
    .filter((e): e is NonNullable<typeof e> => !!e);

  const taken = new Set([ownerId, ...editors.map((e) => e.user_id)]);
  const candidates = employees.filter((e) => e.is_active && !taken.has(e.id));

  return (
    <div className="flex items-center gap-1" aria-label="Medredigerere">
      <Avatar
        name={owner?.full_name ?? "Eier"}
        avatarUrl={owner?.avatar_url}
        size="sm"
        className="ring-1 ring-border"
      />
      {editorUsers.map((user) => (
        <span key={user.id} className="relative group">
          <Avatar name={user.full_name} avatarUrl={user.avatar_url} size="sm" />
          {canManage && (
            <button
              type="button"
              aria-label={`Fjern ${user.full_name}`}
              onClick={() => removeEditor.mutate(user.id)}
              className="absolute -top-1 -right-1 hidden group-hover:flex h-3.5 w-3.5 items-center justify-center rounded-full bg-destructive text-destructive-foreground"
            >
              <SrIcon name="x" className="w-2.5 h-2.5" />
            </button>
          )}
        </span>
      ))}
      {canManage && (
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            <Button
              type="button"
              variant="outline"
              size="icon"
              className="h-6 w-6 rounded-full"
              aria-label="Legg til medredigerer"
              title="Legg til medredigerer"
            >
              <SrIcon name="plus" className="w-3 h-3" />
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-64 p-0" align="start">
            <Command>
              <CommandInput placeholder="Søk kollega…" />
              <CommandList>
                <CommandEmpty>Ingen treff</CommandEmpty>
                <CommandGroup>
                  {candidates.map((user) => (
                    <CommandItem
                      key={user.id}
                      value={user.full_name}
                      onSelect={() => {
                        addEditor.mutate(user.id);
                        setOpen(false);
                      }}
                    >
                      <Avatar
                        name={user.full_name}
                        avatarUrl={user.avatar_url}
                        size="sm"
                        className="mr-2"
                      />
                      {user.full_name}
                    </CommandItem>
                  ))}
                </CommandGroup>
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>
      )}
    </div>
  );
}
